/**
 * Owner-facing insights panel for the Activity Feed page.
 * Renders 5 sections in one panel:
 *   1. Today at a glance (3 KPI cards)
 *   2. Silent counsellor alert banner
 *   3. Per-counsellor leaderboard chips
 *   4. Status funnel chart
 *   5. SLA breach tab content (rendered separately by parent)
 *
 * Lives ABOVE the chronological event list in ActivityFeedPage.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  TrendingUp,
  AlertTriangle,
  Trophy,
  Clock,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ActivityInsightsPanel({
  sinceIso,
  onCounsellorClick,
  refreshKey,
}) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (sinceIso) params.since = sinceIso;
        const r = await api.get("/admin/activity-feed/insights", { params });
        if (!cancelled) setInsights(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sinceIso, refreshKey]);

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500"
        data-testid="insights-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Crunching team numbers…
      </div>
    );
  }
  if (!insights) return null;

  const { today_glance, leaderboard, silent_counsellors, funnel, sla_breaches } =
    insights;

  return (
    <div className="space-y-3" data-testid="activity-insights-panel">
      {/* Silent counsellor alert banner */}
      {silent_counsellors?.length > 0 && (
        <div
          className="flex items-start gap-2.5 rounded-lg border border-rose-300 bg-rose-50 p-3"
          data-testid="silent-counsellor-alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
          <div className="min-w-0 flex-1 text-sm">
            <p className="font-semibold text-rose-900">
              {silent_counsellors.length} counsellor
              {silent_counsellors.length > 1 ? "s" : ""} need
              {silent_counsellors.length > 1 ? "" : "s"} attention
            </p>
            <ul className="mt-1 space-y-0.5">
              {silent_counsellors.slice(0, 5).map((c) => (
                <li
                  key={c.id}
                  className="text-xs text-rose-800"
                  data-testid={`silent-counsellor-${c.id}`}
                >
                  <span className="font-medium">{c.name}</span>
                  <span className="text-rose-700"> · {c.detail}</span>
                </li>
              ))}
              {silent_counsellors.length > 5 && (
                <li className="text-xs italic text-rose-700">
                  +{silent_counsellors.length - 5} more…
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Today at a glance — 3 KPI cards */}
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
        <KpiCard
          icon={Phone}
          label="Calls made"
          value={today_glance.calls_made}
          color="emerald"
          testid="kpi-calls"
        />
        <KpiCard
          icon={MessageCircle}
          label="Leads contacted"
          value={today_glance.leads_contacted}
          color="sky"
          testid="kpi-leads-contacted"
          hint="Unique leads touched via call/WA"
        />
        <KpiCard
          icon={TrendingUp}
          label="Status moves"
          value={today_glance.status_moves}
          color="violet"
          testid="kpi-status-moves"
          breakdown={today_glance.status_moves_breakdown}
        />
      </div>

      {/* SLA breaches summary card (clickable to open the SLA tab) */}
      {sla_breaches?.total > 0 && (
        <button
          type="button"
          onClick={() => nav("/admin?archived=false&sort=sla_breach")}
          className="flex w-full items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-left transition-colors hover:bg-amber-100"
          data-testid="sla-breach-summary"
        >
          <Clock className="h-5 w-5 shrink-0 text-amber-700" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-amber-900">
              {sla_breaches.total} lead{sla_breaches.total > 1 ? "s" : ""}{" "}
              breaching SLA
            </p>
            <p className="text-xs text-amber-800">
              Oldest:{" "}
              {sla_breaches.samples[0]?.name || "—"}
              {sla_breaches.samples[0]?.owner_name &&
                ` · owned by ${sla_breaches.samples[0].owner_name}`}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-amber-700" />
        </button>
      )}

      {/* Leaderboard — counsellor activity chips */}
      {leaderboard?.length > 0 && (
        <div
          id="leaderboard"
          className="scroll-mt-32 rounded-lg border border-slate-200 bg-white p-3"
          data-testid="leaderboard-panel"
        >
          <div className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
            <Trophy className="h-3.5 w-3.5" /> Team activity today
          </div>
          <div className="flex flex-wrap gap-1.5">
            {leaderboard.map((row) => {
              const teamAvg =
                leaderboard.reduce((s, r) => s + r.events, 0) /
                Math.max(1, leaderboard.length);
              const tone =
                row.events === 0
                  ? "border-rose-200 bg-rose-50 text-rose-800"
                  : row.events >= teamAvg
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-amber-200 bg-amber-50 text-amber-800";
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => onCounsellorClick?.(row.id)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-transform hover:scale-[1.02] ${tone}`}
                  data-testid={`leaderboard-chip-${row.id}`}
                  title={
                    row.first_reply_median_min != null
                      ? `Median first reply: ${row.first_reply_median_min}m`
                      : "No first-reply data yet"
                  }
                >
                  <span className="font-semibold">{row.name}</span>
                  <span className="opacity-80">·</span>
                  <span>{row.events} events</span>
                  {row.first_reply_median_min != null && (
                    <>
                      <span className="opacity-50">·</span>
                      <span className="font-mono">
                        {row.first_reply_median_min}m
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Funnel chart — horizontal bars */}
      {Object.keys(funnel || {}).length > 0 && (
        <FunnelChart funnel={funnel} />
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, hint, breakdown, testid }) {
  const palette = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
    sky: "border-sky-200 bg-sky-50/60 text-sky-900",
    violet: "border-violet-200 bg-violet-50/60 text-violet-900",
  };
  const iconBg = {
    emerald: "bg-emerald-100 text-emerald-700",
    sky: "bg-sky-100 text-sky-700",
    violet: "bg-violet-100 text-violet-700",
  };
  return (
    <div
      className={`rounded-lg border p-3 ${palette[color] || palette.sky}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <span
          className={`flex h-7 w-7 items-center justify-center rounded-md ${
            iconBg[color] || iconBg.sky
          }`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-[10px] uppercase tracking-wider opacity-80">
          {label}
        </span>
      </div>
      <div className="mt-1.5 text-3xl font-semibold leading-none">{value}</div>
      {hint && <div className="mt-1 text-[10px] opacity-70">{hint}</div>}
      {breakdown && Object.keys(breakdown).length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1 text-[10px]">
          {Object.entries(breakdown)
            .slice(0, 4)
            .map(([k, v]) => (
              <span
                key={k}
                className="rounded-full bg-white/60 px-1.5 py-0.5 font-medium"
              >
                {v} → {k}
              </span>
            ))}
        </div>
      )}
    </div>
  );
}

function FunnelChart({ funnel }) {
  const ORDER = [
    "New",
    "Contacted",
    "Counselled",
    "Zoom Done",
    "Visited",
    "Docs Recd",
    "Enrolled",
    "Closed-lost",
  ];
  const rows = ORDER.filter((k) => funnel[k] != null).map((k) => ({
    k,
    v: funnel[k],
  }));
  const others = Object.entries(funnel)
    .filter(([k]) => !ORDER.includes(k))
    .map(([k, v]) => ({ k, v }));
  const all = [...rows, ...others];
  const max = Math.max(...all.map((r) => r.v), 1);

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3"
      data-testid="funnel-chart"
    >
      <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
        Status pipeline
      </div>
      <ol className="space-y-1.5">
        {all.map(({ k, v }) => (
          <li key={k} className="flex items-center gap-2 text-xs">
            <span className="w-24 shrink-0 truncate text-slate-700" title={k}>
              {k}
            </span>
            <div className="relative h-5 flex-1 rounded-sm bg-slate-100">
              <div
                className="h-5 rounded-sm bg-[#0A2540] transition-all"
                style={{ width: `${(v / max) * 100}%` }}
              />
              <span className="absolute inset-0 flex items-center pl-2 text-[10px] font-medium text-white mix-blend-difference">
                {v}
              </span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
